<template>
    <div id="manage-attendance-page">
        <!-- HEADER -->
        <div class="action-header">
            <div class="search-container">
                <div class="search-input">
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="search"
                        :placeholder="this.$vuetify.lang.t('$vuetify.manageAttendance.searchByEmployeeName')"
                        solo
                        height="40px"
                        @keyup.enter="getAttendancesPagination()">
                    </v-text-field>
                </div>

                <div class="ml-3">
                    <v-btn
                        @click="searchAttendances()"
                        height="40px"
                        color="primary"
                        outlined>
                        {{ $vuetify.lang.t('$vuetify.manageAttendance.search') }}
                    </v-btn>
                </div>

                <div class="ml-day-inquiry">
                    <v-menu
                        ref="inquiryDayMenu"
                        v-model="datePickerShow"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="234px">
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                prepend-inner-icon="event"
                                @click:prepend-inner="datePickerShow = true"
                                v-model="dateFormatted"
                                readonly
                                v-on="on"
                                outlined
                                height="40px"
                                background-color="#ffffff"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            :locale="localeLang"
                            ref="inquiryDayPicker"
                            v-model="inquiryDay"
                            @change="getInquiryByDay"
                        ></v-date-picker>
                    </v-menu>
                </div>
            </div>
        </div>
        <!-- END HEADER -->

        <!-- DATATABLE -->
        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalData"
                :items="items"
                :loading="loading">
                <template v-slot:[`item.status`]="{ item }">
                    <div :class="statusLabel(item.status['key'])">
                        <div>{{ item.status['value'] === 'Present' ? 'Ontime' : item.status['value'] }}</div>
                    </div>
                </template>
                <template v-slot:[`item.action`]="{ item }">
                    <v-icon
                        v-if="permissionUpdate"
                        class="mr-2"
                        @click="updateInquiryStatus(item.id)">
                        edit
                    </v-icon>
                </template>
            </v-data-table>
        </v-card>
        <!-- END DATATABLE -->

        <!-- PAGINATION -->
        <div class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>
        <!-- END PAGINATION -->
    </div>
</template>

<script>
import permissionConstant from '@/constants/permissions.js'
export default {
    name: 'ManageAttendance',

    data () {
        return {
            // Filter
            search: '',
            inquiryDay: this.$util.getCurrentDate(),
            datePickerShow: false,
            dateFormatted: this.$util.formatDate(this.$util.getCurrentDate()),
            pageCount: 1,
            page: 1,
            options: {},
            items: [],
            totalData: 0,
            loading: false,
            branch: null,
            permissionUpdate: true
        }
    },

    computed: {
        localeLang () {
            return this.$vuetify.lang.current
        },
        baseChoiceClass () {
            if (this.loading) {
                return 'choice disable'
            }

            return 'choice'
        },
        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.employeeName'), value: 'employee__fullname', width: '20%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.group'), value: 'group__name', width: '13%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.department'), value: 'employee__department__name', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.date'), value: 'date', width: '14%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.checkIn'), value: 'time', width: '14%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.status'), value: 'status', width: '12%' },
                { text: '', value: 'action', width: '12%', align: 'center', sortable: false }
            ]
        }
    },

    methods: {
        async searchAttendances () {
            this.options.page = 1
            await this.getAttendancesPagination()
        },
        async getAttendancesPagination () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options

            let url = `attendances?branch=${this.branch}&inquiryDay=${this.inquiryDay}&page=${page}&itemsPerPage=${itemsPerPage}`

            if (sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.items = res.data.data
                this.totalData = res.data.total
                this.loading = false
            }
        },

        async getInquiryByDay (date) {
            this.$refs.inquiryDayMenu.save(date)
            this.dateFormatted = this.$util.formatDate(date)
            await this.getAttendancesPagination()
        },

        async getBranch () {
            this.branch = localStorage.getItem('currentBranchId')
            if (this.branch) {
                await this.getAttendancesPagination()
            }
        },

        choiceClass (status, choice) {
            if (status === choice) {
                return `${this.baseChoiceClass} active`
            }

            return this.baseChoiceClass
        },

        classForNoPermissionUpdate (status) {
            if (status === 'PRS') {
                return 'ontime-status'
            } else if (status === 'LAT') {
                return 'late-status'
            } else {
                return 'absent-status'
            }
        },

        statusLabel (status) {
            let style = 'status-label'
            if (status === 'OSF' || status === 'PRS' || status === 'OSH') {
                style += ' ontime'
            } else if (status === 'LAT') {
                style += ' late'
            } else if (status === 'ABS') {
                style += ' absent'
            }
            return style
        },

        updateInquiryStatus (id) {
            const dateSelect = this.inquiryDay
            this.$router.push({ name: 'AttendanceForm', params: { id, dateSelect } }, () => {})
        },

        checkPermission () {
            this.permissionUpdate = this.$util.checkPermission(permissionConstant.attendance.update)
        }
    },

    async mounted () {
        await this.getBranch()
        const { dateSelect } = this.$route.params
        if (dateSelect) {
            this.inquiryDay = new Date(dateSelect).toISOString().substr(0, 10)
            this.dateFormatted = this.$util.formatDate(new Date(dateSelect).toISOString().substr(0, 10))
        }
        this.checkPermission()
    },

    watch: {
        options: {
            async handler () {
                if (this.branch) {
                    await this.getAttendancesPagination()
                }
            }
        },
        '$vuetify.lang.current': {
            handler: async function (after, before) {
                await this.getAttendancesPagination()
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
::v-deep .v-input__control > .v-input__slot {
    min-height: auto !important;
    display: flex !important;
    align-items: center !important;
}

::v-deep .search-input .v-input__control > .v-input__slot {
    box-shadow: none !important;
    border: 1px solid #a8a8a8 !important;
}

::v-deep .v-input__prepend-inner {
    margin: auto !important;
}

.ml-day-inquiry {
    margin-left: 40px;
}

.show-status {
    padding: 5px 10px;
    border-radius: 25px;
}

.status-label {
    display: flex;
    color: white;
    font-size: 14px;
    justify-content: flex-start;
    align-items: center;

    div {
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 15px;
        padding: 0px 15px;
        width: 80px;
    }

    &.late > div {
        background-color: #d76436;
    }
    &.ontime > div {
        background-color: #6c98ba;
    }
    &.absent > div {
        background-color: #ff544a;
    }
}
</style>
